import {
  type BladeTarget,
  type PictureTarget,
  type RotationTarget,
} from 'horizon/routes/Inspections/types';
import { computeHomography } from 'horizon/utils/homography';
import {
  type AtlasGqlAsset,
  type AtlasGqlBlade,
  type AtlasGqlTargetBalance_Of_Plant,
  type AtlasGqlTargetBlade_Internal,
  type AtlasGqlTargetGearbox,
  type AtlasGqlTargetGeneric,
  type AtlasGqlTargetNacelle_External,
  type AtlasGqlTargetSolar,
  type AtlasGqlTargetSolar_Back_Of_Panel,
  type AtlasGqlTargetSolar_Combiner,
  type AtlasGqlTargetSolar_Inverter,
  type AtlasGqlTargetTower_External,
  type AtlasGqlTargetTransition_Piece,
  AtlasGqlBladeChamber,
  AtlasGqlBladeSide,
} from 'types/atlas-graphql';
import { BALANCE_OF_PLANT } from 'utils/constants';
import OpenSeaDragon from 'utils/openseadragon4';

import { type Point, type RectCoords } from './TemperatureMeasurementTool/types';
import type { BladePictureLike } from './types';

export const isBladeTarget = (target?: PictureTarget): boolean => {
  return ['TargetBLADE_EXTERNAL', 'TargetBLADE_INTERNAL'].includes(target?.__typename ?? '');
};

export const isRotationTarget = (target?: PictureTarget): boolean => {
  return ['TargetBLADE_EXTERNAL', 'TargetBLADE_INTERNAL', 'TargetNACELLE_EXTERNAL'].includes(
    target?.__typename ?? ''
  );
};

export const isSolarPictureTarget = (target?: PictureTarget | null): boolean => {
  return ['SOLAR', 'SOLAR_BACK_OF_PANEL', 'SOLAR_COMBINER', 'SOLAR_INVERTER'].includes(
    target?.type ?? ''
  );
};

export const isDamageAssetSelectablePictureTarget = (target?: PictureTarget | null): boolean => {
  return isSolarPictureTarget(target) || target?.type === 'BALANCE_OF_PLANT';
};

type DamageAssetSelectablePicture = {
  target?: PictureTarget | null;
  inspection?: { type?: { name?: string | null } | null } | null;
  camera?: { type?: string | null } | null;
};

export const isBalanceOfPlantPicture = (picture?: DamageAssetSelectablePicture | null): boolean =>
  picture?.target?.type === 'BALANCE_OF_PLANT' ||
  picture?.inspection?.type?.name === BALANCE_OF_PLANT;

export const isDamageAssetSelectablePicture = (
  picture?: DamageAssetSelectablePicture | null
): boolean =>
  (isDamageAssetSelectablePictureTarget(picture?.target) || isBalanceOfPlantPicture(picture)) &&
  picture?.camera?.type === 'GEOSPATIAL';

type InferenceBoundaryPoint = [number, number] | { x?: number; y?: number } | null | undefined;

type AssetBackedTarget =
  | AtlasGqlTargetBalance_Of_Plant
  | AtlasGqlTargetGeneric
  | AtlasGqlTargetSolar
  | AtlasGqlTargetSolar_Back_Of_Panel
  | AtlasGqlTargetSolar_Combiner
  | AtlasGqlTargetSolar_Inverter;

export const normalizeInferenceBoundaryCoordinates = (
  boundary: unknown,
  imageWidth?: number | null
): [number, number][] => {
  if (!Array.isArray(boundary)) {
    return [];
  }

  const width = Number(imageWidth);
  const points = boundary
    .map((point: InferenceBoundaryPoint) => {
      if (
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1])
      ) {
        return [Number(point[0]), Number(point[1])] as [number, number];
      }

      if (
        point &&
        !Array.isArray(point) &&
        typeof point === 'object' &&
        Number.isFinite(point.x) &&
        Number.isFinite(point.y)
      ) {
        return [Number(point.x), Number(point.y)] as [number, number];
      }

      return null;
    })
    .filter((point): point is [number, number] => Array.isArray(point));

  const normalizedPoints =
    Number.isFinite(width) && width > 0 && points.some(([x]) => x > 1)
      ? points.map(([x, y]) => [x / width, y / width] as [number, number])
      : points;

  if (normalizedPoints.length === 2) {
    const [[x1, y1], [x2, y2]] = normalizedPoints;
    return [
      [x1, y1],
      [x2, y1],
      [x2, y2],
      [x1, y2],
    ];
  }

  return normalizedPoints.length >= 4 ? normalizedPoints.slice(0, 4) : [];
};

export const isRotationEnabled = (target?: PictureTarget): boolean => {
  return isRotationTarget(target) && getRotationDegrees(target as RotationTarget) !== 0;
};

export const getRotationDegrees = (target?: RotationTarget): number => {
  const componentAngle = isBladeTarget(target)
    ? (target as BladeTarget)?.bladeAngle
    : (target as AtlasGqlTargetNacelle_External)?.componentAngle;

  const radians = Number((componentAngle || '').replace('rad', ''));
  const degrees = radians ? radians * (180 / Math.PI) : 0;

  return degrees;
};

export const getPictureTargetAsset = (target: PictureTarget) => {
  const bladeAsset = (target as BladeTarget)?.bladeAsset as AtlasGqlBlade;
  const tower = (target as AtlasGqlTargetTower_External)?.tower;
  const transitionPiece = (target as AtlasGqlTargetTransition_Piece)?.transitionPiece;
  const nacelle = (target as AtlasGqlTargetNacelle_External)?.nacelle;
  const gearbox = (target as AtlasGqlTargetGearbox)?.gearbox;
  const targetAsset =
    isSolarPictureTarget(target) ||
    target?.type === 'BALANCE_OF_PLANT' ||
    target?.type === 'GENERIC'
      ? (target as AssetBackedTarget)?.asset
      : undefined;
  const asset = bladeAsset ?? tower ?? transitionPiece ?? nacelle ?? gearbox ?? targetAsset;

  return asset as AtlasGqlAsset | undefined;
};

export const sphericalToEquirectangularNormalized = ({
  yaw,
  pitch,
  imageHeight,
  imageWidth,
}: {
  yaw: number;
  pitch: number;
  imageWidth: number;
  imageHeight: number;
}): [number, number] => {
  const x = (yaw + Math.PI) / (2 * Math.PI);
  const y = (((Math.PI / 2 - pitch) / Math.PI) * imageHeight) / imageWidth;
  return [x > 1 ? x - 1 : x, y > 1 ? y - 1 : y];
};

export const normalizedEquirectangularToSpherical = ({
  x,
  y,
  imageHeight,
  imageWidth,
}: {
  x: number;
  y: number;
  imageWidth: number;
  imageHeight: number;
}): { yaw: number; pitch: number } => {
  const yaw = x * 2 * Math.PI - Math.PI;
  const pitch = Math.PI / 2 - y * Math.PI * (imageWidth / imageHeight);
  return { yaw, pitch };
};

/**
 * Converts normalized equirectangular coordinates (0-1) to pixel coordinates
 * @param normalized - Array of [x, y] normalized coordinates (0-1 range)
 * @param imageWidth - Width of the image in pixels
 * @param imageHeight - Height of the image in pixels
 * @returns Array of [x, y] pixel coordinates
 */
export const normalizedToPixelCoordinates = (
  normalized: [number, number],
  imageWidth: number,
  imageHeight: number
): [number, number] => {
  return [Math.round(normalized[0] * imageWidth), Math.round(normalized[1] * imageHeight)];
};

export const radToDeg = (rad: number = 0): number => {
  return (rad * 180) / Math.PI;
};

export const degToRad = (deg: number): number => {
  return (deg * Math.PI) / 180;
};

export const convertSphericalToDegrees = (yaw: number, pitch: number) => {
  let yawD = radToDeg(yaw);
  yawD = yawD > 0 ? 360 - yawD : -yawD; // adjust for viewer coordinates
  const pitchD = radToDeg(pitch);
  return { yaw: yawD, pitch: pitchD };
};

export const getPixelPointFromViewportPoint = (
  viewer: OpenSeaDragon.Viewer,
  Width: number,
  point: [number, number]
) => {
  const imagePoint = new OpenSeaDragon.Point(point[0] * Width, point[1] * Width);
  const viewPortPoint = viewer.viewport.imageToViewportCoordinates(imagePoint);
  const pixelPoint = viewer.viewport.pixelFromPoint(viewPortPoint, true);
  return pixelPoint;
};

export const getAngleAndPerpendicularValues = (
  px1: OpenSeaDragon.Point,
  px2: OpenSeaDragon.Point,
  side: 'above' | 'below'
) => {
  /* Screen-space direction + angle */
  const dx = px2.x - px1.x;
  const dy = px2.y - px1.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;

  /* Keep upright (optional) */
  if (angle > 90 || angle < -90) {
    angle += 180;
  }

  /* Screen-space perpendicular */
  let perpX = -dy / len;
  let perpY = dx / len;

  /* Normalize side */
  if (side === 'above') {
    if (perpY > 0) {
      perpX = -perpX;
      perpY = -perpY;
    }
  } else {
    if (perpY < 0) {
      perpX = -perpX;
      perpY = -perpY;
    }
  }

  return {
    angle,
    perpX,
    perpY,
  };
};

export const getTemperatureTextValue = ({
  temperature,
  diffTemperature,
}: {
  temperature: number;
  diffTemperature?: number;
}): string => {
  let temperatureValue = `${temperature}° C`;
  if (diffTemperature !== undefined) {
    temperatureValue = `ΔT ${diffTemperature}° C`;
  }
  return temperatureValue;
};

export const getRectCoords = ([p1, p2]: [Point, Point]) => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const rectX = Math.min(x1, x2);
  const rectY = Math.min(y1, y2);
  const rectWidth = Math.abs(x1 - x2);
  const rectHeight = Math.abs(y1 - y2);
  return {
    rectX,
    rectY,
    rectWidth,
    rectHeight,
  };
};

export const getRectTopCenterFromCoords = (rectCoords: RectCoords): [number, number] => {
  const { rectX, rectY, rectWidth } = rectCoords;

  const topCenterX = rectX + rectWidth / 2;
  const topCenterY = rectY;

  return [topCenterX, topCenterY];
};

export const getOverlayPositions = ({
  viewer,
  Width,
  rectPoints,
}: {
  viewer: OpenSeaDragon.Viewer;
  Width: number;
  rectPoints: [Point, Point];
}) => {
  const rectCoords = getRectCoords(rectPoints);
  const { rectX, rectY, rectWidth, rectHeight } = rectCoords;

  // Build all 4 rectangle corners in normalized image space
  const corners: [number, number][] = [
    [rectX, rectY], // top-left
    [rectX + rectWidth, rectY], // top-right
    [rectX + rectWidth, rectY + rectHeight], // bottom-right
    [rectX, rectY + rectHeight], // bottom-left
  ];

  // Convert all corners to SCREEN pixel coordinates
  const pixelCorners = corners.map(point => getPixelPointFromViewportPoint(viewer, Width, point));

  // Sort by Y (smallest Y = visually highest)
  pixelCorners.sort((a, b) => a.y - b.y);

  // Take the top 2 points and compute midpoint
  const top1 = pixelCorners[0];
  const top2 = pixelCorners[1];

  const topCenterX = (top1.x + top2.x) / 2;
  const topCenterY = (top1.y + top2.y) / 2;

  return {
    left: topCenterX,
    top: topCenterY,
  };
};

export const parsePolygonValuesFromString = (polygonString: string): [number, number][] => {
  return polygonString
    .replace(/^POLYGON\s*\(\(/, '')
    .replace(/\)\)$/, '')
    .split(',')
    .map(pair => pair.trim().split(/\s+/).map(Number) as [number, number])
    .slice(0, 4);
};

export const getMinMaxValues = (corners: number[][]) => {
  // Find min/max lon & lat from a given polygon
  const latitudes = corners.map(p => p[0]);
  const longitudes = corners.map(p => p[1]);

  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);

  return {
    minLon,
    maxLon,
    minLat,
    maxLat,
  };
};

export const polygonToPixels = (
  polygonString: string,
  width: number,
  height: number,
  corners: number[][],
  options?: {
    imageToMapHomography?: string | number[] | null;
    headingDeg?: number;
  }
) => {
  const polygon = parsePolygonValuesFromString(polygonString);

  const worldToMapHomography = getCornerHomography(corners, width, height);
  const mapToDisplayHomography = getMapToDisplayHomography({
    imageToMapHomography: options?.imageToMapHomography,
    headingDeg: options?.headingDeg,
    width,
    height,
  });
  const worldToDisplayHomography =
    worldToMapHomography && mapToDisplayHomography
      ? multiply3x3(mapToDisplayHomography, worldToMapHomography)
      : worldToMapHomography;

  const { maxLat, maxLon, minLat, minLon } = getMinMaxValues(corners);

  // Convert each point to pixel space and normalize by dividing width.
  const pixels: [number, number][] = polygon.map(([lon, lat]) => {
    if (worldToDisplayHomography) {
      const projected = applyHomography(worldToDisplayHomography, [lon, lat]);
      if (projected) {
        const [x, y] = projected;
        return [x / width, y / width];
      }
    }

    const x = (((lon - minLon) / (maxLon - minLon)) * width) / width;
    const y = (((maxLat - lat) / (maxLat - minLat)) * height) / width;
    return [x, y];
  });

  return {
    bounds: { minLon, maxLon, minLat, maxLat },
    pixels,
  };
};

const getCornerHomography = (corners: number[][], width: number, height: number) => {
  if (corners.length !== 4) {
    return null;
  }
  if (
    corners.some(
      corner =>
        corner.length < 2 ||
        typeof corner[0] !== 'number' ||
        typeof corner[1] !== 'number' ||
        !Number.isFinite(corner[0]) ||
        !Number.isFinite(corner[1])
    )
  ) {
    return null;
  }

  const src: [number, number][] = [
    [corners[0][1], corners[0][0]],
    [corners[1][1], corners[1][0]],
    [corners[2][1], corners[2][0]],
    [corners[3][1], corners[3][0]],
  ];
  const dst: [number, number][] = [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ];

  return computeHomography(src, dst);
};

const applyHomography = (h: number[], point: [number, number]) => {
  const [x, y] = point;
  const denom = h[6] * x + h[7] * y + h[8];
  if (Math.abs(denom) < 1e-10) {
    return null;
  }
  const u = (h[0] * x + h[1] * y + h[2]) / denom;
  const v = (h[3] * x + h[4] * y + h[5]) / denom;
  return [u, v] as [number, number];
};

const getMapToDisplayHomography = ({
  imageToMapHomography,
  headingDeg,
  width,
  height,
}: {
  imageToMapHomography?: string | number[] | null;
  headingDeg?: number;
  width: number;
  height: number;
}) => {
  const normalizedImageToMap = parseMatrix3dTo2DHomography(imageToMapHomography);
  if (!normalizedImageToMap) {
    return null;
  }

  // Existing picture homography maps normalized image coordinates.
  // Convert pixel coordinates to normalized before applying it.
  const pixelToNormalized = [1 / width, 0, 0, 0, 1 / height, 0, 0, 0, 1];
  const pixelToMap = multiply3x3(normalizedImageToMap, pixelToNormalized);
  const mapToPixel = invert3x3(pixelToMap);
  if (!mapToPixel) {
    return null;
  }

  const headingOnly = Number.isFinite(headingDeg)
    ? getRotationAroundCenterHomography(width, height, headingDeg as number)
    : IDENTITY_3X3;

  return multiply3x3(headingOnly, mapToPixel);
};

const parseMatrix3dTo2DHomography = (value?: string | number[] | null) => {
  if (!value) {
    return null;
  }

  let matrix: number[];
  if (Array.isArray(value)) {
    matrix = value;
  } else {
    try {
      const parsed = JSON.parse(value);
      matrix = Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return null;
    }
  }

  if (matrix.length !== 16 || matrix.some(v => !Number.isFinite(v))) {
    return null;
  }

  // CSS matrix3d is column-major:
  // [m11, m12, m13, m14, m21, m22, ... m41, m42, m43, m44]
  // Build 2D projective transform [x, y, 1] in row-major form.
  const h = [
    matrix[0],
    matrix[4],
    matrix[12],
    matrix[1],
    matrix[5],
    matrix[13],
    matrix[3],
    matrix[7],
    matrix[15],
  ];

  if (Math.abs(h[8]) < 1e-10) {
    return null;
  }

  const scale = 1 / h[8];
  return h.map(v => v * scale);
};

const IDENTITY_3X3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

const multiply3x3 = (a: number[], b: number[]) => {
  return [
    a[0] * b[0] + a[1] * b[3] + a[2] * b[6],
    a[0] * b[1] + a[1] * b[4] + a[2] * b[7],
    a[0] * b[2] + a[1] * b[5] + a[2] * b[8],
    a[3] * b[0] + a[4] * b[3] + a[5] * b[6],
    a[3] * b[1] + a[4] * b[4] + a[5] * b[7],
    a[3] * b[2] + a[4] * b[5] + a[5] * b[8],
    a[6] * b[0] + a[7] * b[3] + a[8] * b[6],
    a[6] * b[1] + a[7] * b[4] + a[8] * b[7],
    a[6] * b[2] + a[7] * b[5] + a[8] * b[8],
  ];
};

const invert3x3 = (m: number[]) => {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = f * g - d * i;
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-10) {
    return null;
  }
  const invDet = 1 / det;
  return [
    A * invDet,
    (c * h - b * i) * invDet,
    (b * f - c * e) * invDet,
    B * invDet,
    (a * i - c * g) * invDet,
    (c * d - a * f) * invDet,
    C * invDet,
    (b * g - a * h) * invDet,
    (a * e - b * d) * invDet,
  ];
};

const getRotationAroundCenterHomography = (width: number, height: number, deg: number) => {
  const radians = (deg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const cx = width / 2;
  const cy = height / 2;

  const translateToCenter = [1, 0, -cx, 0, 1, -cy, 0, 0, 1];
  const rotate = [cos, -sin, 0, sin, cos, 0, 0, 0, 1];
  const translateBack = [1, 0, cx, 0, 1, cy, 0, 0, 1];

  return multiply3x3(translateBack, multiply3x3(rotate, translateToCenter));
};

export const buildEncodedPolygonParam = ([pointA, pointB]: [Point, Point]): string => {
  const [x1, y1] = pointA;
  const [x2, y2] = pointB;

  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);

  const polygon: Point[] = [
    [left, top],
    [right, top],
    [right, bottom],
    [left, bottom],
  ];

  return encodeURIComponent(JSON.stringify(polygon));
};

const ORDERED_BLADE_CHAMBERS = [
  AtlasGqlBladeChamber.Root,
  AtlasGqlBladeChamber.LeadingEdge,
  AtlasGqlBladeChamber.BetweenWeb_1,
  AtlasGqlBladeChamber.BetweenWeb_2,
  'between_web_3',
  AtlasGqlBladeChamber.TrailingEdge,
] as const;

const ORDERED_BLADE_SIDES = [
  AtlasGqlBladeSide.PressureSide,
  AtlasGqlBladeSide.LeadingEdge,
  AtlasGqlBladeSide.SuctionSide,
  AtlasGqlBladeSide.TrailingEdge,
] as const;

const getFiniteRadialDistance = (radialDistance?: number | null) => {
  return typeof radialDistance === 'number' && Number.isFinite(radialDistance)
    ? radialDistance
    : null;
};

const getBladePictureTarget = (picture?: BladePictureLike | null) => {
  return picture?.target as AtlasGqlTargetBlade_Internal | undefined;
};

const getPictureRadialDistance = (picture?: BladePictureLike | null) => {
  return getFiniteRadialDistance(getBladePictureTarget(picture)?.radialDistance);
};

const isPictureInRadialBucket = (
  radialDistance: number,
  bucketStart: number,
  bucketEnd: number,
  isFirstBucket: boolean
) => {
  if (isFirstBucket) {
    return radialDistance >= bucketStart && radialDistance <= bucketEnd;
  }

  return radialDistance > bucketStart && radialDistance <= bucketEnd;
};

const getOrderedDistance = <T extends string>(
  orderedValues: readonly T[],
  currentValue?: string | null,
  candidateValue?: string | null
) => {
  if (!currentValue || !candidateValue) {
    return Number.POSITIVE_INFINITY;
  }

  const currentIndex = orderedValues.indexOf(currentValue as T);
  const candidateIndex = orderedValues.indexOf(candidateValue as T);

  if (currentIndex === -1 || candidateIndex === -1) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.abs(candidateIndex - currentIndex);
};

const compareStringValues = (left?: string | null, right?: string | null) => {
  return (left ?? '').localeCompare(right ?? '');
};

export const getClosestPictureForMarker = <T extends BladePictureLike>({
  pictures,
  bucketStart,
  bucketEnd,
  currentPicture,
}: {
  pictures?: Array<T | null> | null;
  bucketStart: number;
  bucketEnd: number;
  currentPicture?: T | null;
}): T | null => {
  if (!pictures?.length) {
    return null;
  }

  const midpoint = (bucketStart + bucketEnd) / 2;
  const isFirstBucket = bucketStart <= 0;
  const currentBladeChamber = getBladePictureTarget(currentPicture)?.bladeChamber ?? null;
  const currentBladeSide = getBladePictureTarget(currentPicture)?.bladeSide ?? null;

  const candidates = pictures
    .filter((picture): picture is T => Boolean(picture))
    .map((picture, index) => {
      const radialDistance = getPictureRadialDistance(picture);
      if (radialDistance == null) {
        return null;
      }

      if (!isPictureInRadialBucket(radialDistance, bucketStart, bucketEnd, isFirstBucket)) {
        return null;
      }

      const pictureTarget = getBladePictureTarget(picture);

      const chamberDistance = currentBladeChamber
        ? getOrderedDistance(
            ORDERED_BLADE_CHAMBERS,
            currentBladeChamber,
            pictureTarget?.bladeChamber ?? null
          )
        : 0;

      const sideDistance = currentBladeSide
        ? getOrderedDistance(
            ORDERED_BLADE_SIDES,
            currentBladeSide,
            pictureTarget?.bladeSide ?? null
          )
        : 0;

      return {
        picture,
        index,
        radialDistance,
        chamberDistance,
        sideDistance,
        radialDelta: Math.abs(radialDistance - midpoint),
        bladeChamber: pictureTarget?.bladeChamber ?? null,
        bladeSide: pictureTarget?.bladeSide ?? null,
      };
    })
    .filter(Boolean) as Array<{
    picture: T;
    index: number;
    radialDistance: number;
    chamberDistance: number;
    sideDistance: number;
    radialDelta: number;
    bladeChamber: string | null;
    bladeSide: AtlasGqlBladeSide | string | null;
  }>;

  if (!candidates.length) {
    return null;
  }

  candidates.sort((left, right) => {
    if (left.chamberDistance !== right.chamberDistance) {
      return left.chamberDistance - right.chamberDistance;
    }

    if (left.sideDistance !== right.sideDistance) {
      return left.sideDistance - right.sideDistance;
    }

    if (left.radialDelta !== right.radialDelta) {
      return left.radialDelta - right.radialDelta;
    }

    const chamberComparison = compareStringValues(left.bladeChamber, right.bladeChamber);
    if (chamberComparison !== 0) {
      return chamberComparison;
    }

    const sideComparison = compareStringValues(left.bladeSide, right.bladeSide);
    if (sideComparison !== 0) {
      return sideComparison;
    }

    return left.index - right.index;
  });

  return candidates[0]?.picture ?? null;
};

export function getClosestPictureByRadial<T extends { id: string; target?: BladeTarget }>(
  pictures: T[],
  currentRadial?: number | null
): T | null {
  if (!pictures || pictures.length === 0) {
    return null;
  }

  if (currentRadial === null || currentRadial === undefined) {
    return pictures[0] ?? null;
  }
  const radialDistance = Number(currentRadial);
  if (!Number.isFinite(radialDistance)) {
    return pictures[0] ?? null;
  }

  const picturesWithRadial = pictures.filter(pic => {
    const radial = (pic.target as BladeTarget)?.radialDistance;
    return typeof radial === 'number' && Number.isFinite(radial);
  });

  if (!picturesWithRadial.length) {
    return pictures[0] ?? null;
  }

  return picturesWithRadial.reduce((closestPicture, currentPicture) => {
    const currentPictureRadial = (currentPicture.target as BladeTarget).radialDistance as number;
    const closestPictureRadial = (closestPicture.target as BladeTarget).radialDistance as number;
    return Math.abs(currentPictureRadial - radialDistance) <
      Math.abs(closestPictureRadial - radialDistance)
      ? currentPicture
      : closestPicture;
  }, picturesWithRadial[0]);
}
